import { useState } from 'react';
import { Button, Switch, Select, Loader, Textarea, ActionIcon, Combobox, useCombobox } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useUpdateServiceBooking } from '@/hooks/api-hooks';
import { useDynamicOptions } from '@/lib/dynamic-options/attr-dynamic-options';
import { TagIcon } from '@/lib/tag-utils';
import { CustomAttr, ServiceBooking } from '@turizi/shared';
import { IconPlus, IconTrash } from '@tabler/icons-react';

interface AutocompleteTextareaProps {
    className?: string;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    value?: string;
    data: any[];
    isLoading?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    onChange: (value: string) => void;
    renderOption?: (item: { option: any }) => React.ReactNode;
}

function AutocompleteTextarea({
    className,
    disabled,
    label,
    placeholder,
    value = '',
    data,
    isLoading,
    onFocus,
    onBlur,
    onChange,
    renderOption,
}: AutocompleteTextareaProps) {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const filteredData = (data || []).filter((item: any) => {
        const itemVal = typeof item === 'string' ? item : item?.value;
        const query = (value || '').toLowerCase().trim();
        if (!query) return true;
        return itemVal?.toLowerCase().includes(query);
    });

    const options = filteredData.map((item: any, idx: number) => {
        const itemVal = typeof item === 'string' ? item : item?.value;
        return (
            <Combobox.Option value={itemVal} key={`${itemVal}-${idx}`}>
                {renderOption ? renderOption({ option: typeof item === 'string' ? { value: item } : item }) : itemVal}
            </Combobox.Option>
        );
    });

    return (
        <Combobox
            store={combobox}
            withinPortal={false}
            onOptionSubmit={(val) => {
                onChange(val);
                combobox.closeDropdown();
            }}
        >
            <Combobox.Target>
                <Textarea
                    className={className}
                    disabled={disabled}
                    label={label}
                    placeholder={placeholder}
                    value={value}
                    autosize
                    minRows={1}
                    maxRows={6}
                    onFocus={() => {
                        onFocus?.();
                        combobox.openDropdown();
                    }}
                    onBlur={() => {
                        onBlur?.();
                        combobox.closeDropdown();
                    }}
                    onChange={(e) => {
                        onChange(e.currentTarget.value);
                        combobox.openDropdown();
                        combobox.updateSelectedOptionIndex();
                    }}
                    onClick={() => combobox.openDropdown()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            if (combobox.getSelectedOptionIndex() === -1) {
                                e.preventDefault();
                                combobox.closeDropdown();
                                (e.target as HTMLElement).blur();
                            }
                        }
                    }}
                    rightSection={
                        isLoading ? <Loader size="xs" color="black" /> : null
                    }
                />
            </Combobox.Target>

            {options.length > 0 && (
                <Combobox.Dropdown>
                    <Combobox.Options mah={200} style={{ overflowY: 'auto' }}>
                        {options}
                    </Combobox.Options>
                </Combobox.Dropdown>
            )}
        </Combobox>
    );
}

interface Props {
    booking: ServiceBooking;
    onSave: () => void;
}

export default function EditBookingAttrsSection({ booking, onSave }: Props) {
    const { t } = useTranslation();
    const [attrs, setAttrs] = useState<CustomAttr[]>(booking.self_attrs || []);
    const [observations, setObservations] = useState(booking?.observations || '');
    const [currentAttr, setCurrentAttr] = useState<CustomAttr | null>(null);

    const {
        mutate: updateBooking,
        isPending: saving,
    } = useUpdateServiceBooking();

    // Get dynamic options/suggestions for the focused attribute
    const {
        data: suggestions,
        isLoading: isLoadingSuggestions
    } = useDynamicOptions(
        currentAttr,
        {
            type: 'service_booking',
            id: booking.id
        }
    );

    const handleSave = () => {
        updateBooking({
            id: booking.id,
            reservation_id: booking.reservation_id,
            self_attrs: attrs,
            observations: observations,
        }, {
            onSuccess: () => {
                onSave();
            }
        });
    };

    const handleAttrChange = (index: number, value: any) => {
        setAttrs(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], value };
            return copy;
        });
    };

    const hasChanges = () => {
        if (attrs.length !== (booking.self_attrs?.length || 0)) return true;
        return attrs.some((attr, index) => {
            const original = booking.self_attrs?.[index];
            return attr.key !== original?.key || attr.value !== original?.value;
        }) || observations !== booking?.observations;
    };

    const handleDuplicateAttr = (index: number) => {
        setAttrs(prev => {
            const sourceAttr = prev[index];
            const baseKey = sourceAttr.key?.split('#')?.[0] || sourceAttr.key;

            const count = prev.filter(attr => (attr.key?.split('#')?.[0] || attr.key) === baseKey).length;
            const newKey = `${baseKey}#${count + 1}`;

            const newAttr: CustomAttr = {
                ...sourceAttr,
                key: newKey
            };
            const copy = [...prev];
            copy.splice(index + 1, 0, newAttr);
            return copy;
        });
    };

    const handleRemoveAttr = (index: number) => {
        setAttrs(prev => prev.filter((_, i) => i !== index));
    };

    const renderOption = ({ option }: { option: any }) => {
        const originalSuggestion = suggestions?.find((s: any) => s.value === option.value);
        const cat = originalSuggestion?.key;
        return (
            <div className='flex items-center gap-2 py-1'>
                <TagIcon name={cat} noBackground hideText size='xxs' />
                <span>{option.value}</span>
            </div>
        );
    };

    return (

        <div>
            <div className="flex flex-col gap-1">
                {attrs.map((attr, index) => {
                    const baseKey = attr.key?.split('#')?.[0] || attr.key;
                    const isLastOfBaseKey = !attrs.slice(index + 1).some(a => (a.key?.split('#')?.[0] || a.key) === baseKey);
                    const isDuplicate = attr.key.includes('#');
                    const label = t(baseKey) + (attr.key.split('#')?.[1] ? ` #${attr.key.split('#')?.[1]}` : '');

                    if (attr.type === 'boolean') {
                        return (
                            <div key={`${attr.key}-${index}`} className="flex items-center justify-between py-1 border-b border-slate-100/60 pb-2">
                                <span className="text-xs font-semibold text-slate-700">{label}</span>
                                <Switch
                                    checked={!!attr.value}
                                    onChange={(e) => handleAttrChange(index, e.currentTarget.checked)}
                                    color="black"
                                />
                            </div>
                        );
                    }

                    if (attr.options && attr.options.length > 0) {
                        return (
                            <Select
                                disabled={saving}
                                key={`${attr.key}-${index}`}
                                label={label}
                                data={attr.options.map(o => ({ value: o, label: t(o) }))}
                                value={attr.value || ''}
                                onChange={(val) => handleAttrChange(index, val || '')}
                                placeholder={attr.placeholder || t('select_option')}
                                comboboxProps={{
                                    withinPortal: false,
                                    keepMounted: false,
                                }}
                                clearable
                                searchable
                            />
                        );
                    }

                    return (
                        <div key={`${attr.key}-${index}`} className="flex flex-col gap-1 solid-box-border-tinted">
                            <div className="flex items-end gap-1">
                                <AutocompleteTextarea
                                    className="flex-1"
                                    disabled={saving}
                                    label={label}
                                    data={currentAttr?.key === attr.key ? (suggestions || []) : []}
                                    placeholder={attr.placeholder || ''}
                                    value={attr.value || ''}
                                    onFocus={() => setCurrentAttr(attr)}
                                    onBlur={() => setCurrentAttr(null)}
                                    onChange={(val) => handleAttrChange(index, val)}
                                    renderOption={renderOption}
                                    isLoading={currentAttr?.key === attr.key && (isLoadingSuggestions || saving)}
                                />
                                {isDuplicate && (
                                    <ActionIcon
                                        color="gray"
                                        variant="subtle"
                                        mb={4}
                                        onClick={() => handleRemoveAttr(index)}
                                        disabled={saving}
                                    >
                                        <IconTrash size={16} color="gray" />
                                    </ActionIcon>
                                )}
                            </div>
                            {isLastOfBaseKey && (
                                <div className="flex justify-start mb-2">
                                    <Button
                                        color="gray"
                                        variant="subtle"
                                        size="xs"
                                        px={0}
                                        leftSection={<IconPlus size={14} />}
                                        onClick={() => handleDuplicateAttr(index)}
                                        disabled={saving}
                                    >
                                        {t('add_another_field', { key: t(baseKey) })}
                                    </Button>
                                </div>
                            )}
                        </div>
                    );
                })}
                <div className="solid-box-border-tinted">
                    <label className="text-xs font-semibold text-slate-700">{t('observations')}</label>
                    <Textarea
                        disabled={saving}
                        className="w-full"
                        size="sm"
                        value={observations}
                        rows={1}
                        autosize
                        onChange={(e) => setObservations(e.target.value)}
                    />
                </div>
            </div>

            <div className="mt-1">
                <Button
                    fullWidth
                    color="black"
                    onClick={handleSave}
                    loading={saving}
                    size="xs"
                    disabled={!hasChanges()}
                >
                    {t('save')}
                </Button>
            </div>
        </div>

    );
}
